import Phaser from 'phaser';

/**
 * The Variety Orb cutscene — the campaign's send-off.
 *
 * Ten seconds, full screen, played on EVERY win of Level 25 (*The Ember and
 * Flame*) — it is the level's send-off, so beating the finale again replays
 * it. The orb itself is a one-time prize: the claim happens in GameScene and
 * only the first win banks it, which is why nothing in here touches progress.
 * Three hambis stand in a meadow watching the stars; the rainbow orb falls out
 * of the sky, circles
 * them faster and faster, drops at their feet and floods the screen with white
 * light. The white is the cut — the Level Complete overlay is built underneath
 * it and the white fades off to reveal it.
 *
 * Written as an overlay over the HOST scene, in the style of `bagOpening.ts`,
 * rather than as a registered Phaser scene. The cutscene has to hand control
 * back to a Level Complete overlay that lives *inside* GameScene; a scene swap
 * would tear that context down and there would be nothing to come back to.
 *
 * The `bg-embercave` art the finale is played on already shows a night sky of
 * constellations with a multicoloured orb glowing in the cave mouth — this is
 * that orb, so the scene is a pay-off rather than a non sequitur.
 */

/** Logical canvas. Everything below is authored against 1280 × 720. */
const W = 1280;
const H = 720;

/** Above every GameScene layer AND its win overlay (which sits at 50). */
const DEPTH = 500;

/**
 * Beat boundaries in ms — the whole thing is ten seconds. A skip does not jump
 * past the end: it runs the same white cut on a short duration, so the scene
 * always resolves the same way rather than snapping to the result screen.
 */
const T_FADE_IN = 1500;
const T_STARGAZE_END = 4000;
const T_ORBIT_END = 8000;
const T_LANDED = 9000;
const T_TOTAL = 10000;
const FLASH_MS = T_TOTAL - T_LANDED;

/**
 * The horizon in `bg-cutscene-grassland` sits at ~y=490; the hambis stand below
 * it. They are kept high enough on the grass to leave a clear strip of
 * foreground in FRONT of them for the orb to land in — dropped level with them
 * it simply covered the middle hambi's face.
 */
const GROUND_Y = 556;
const HAMBI_XS = [470, 640, 810];
const HAMBI_SIZE = 132;

/** Orbit geometry: a wide, shallow ellipse centred on the trio. */
const ORBIT_CX = 640;
const ORBIT_CY = 505;
const ORBIT_RX = 300;
const ORBIT_RY = 95;
const ORB_SIZE = 96;
/** Where the orb comes to rest: the grass in front of the trio, clear of them. */
const ORB_LAND_Y = 658;
/** How much it swells as the light builds (kept so it stays on-canvas). */
const ORB_SWELL = 1.4;

/**
 * Play the cutscene over `scene`, calling `onDone` once the screen is fully
 * white — the caller draws its result screen at that moment, and the white
 * then fades off over it.
 *
 * Returns a function that ends the cutscene early, which the caller uses when
 * the scene is being torn down. Clicking anywhere also skips, jumping to the
 * white cut so the orb is still claimed and the ceremony still resolves.
 */
export function playVarietyOrbCutscene(scene: Phaser.Scene, onDone: () => void): () => void {
  const objs: Phaser.GameObjects.GameObject[] = [];
  const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
    objs.push(o);
    return o;
  };
  let finished = false;

  // Opaque black floor first: the game board is still drawn underneath and must
  // not show through the meadow art for even a frame.
  add(scene.add.rectangle(W / 2, H / 2, W, H, 0x000000).setDepth(DEPTH));

  const bg = add(
    scene.add
      .image(W / 2, H / 2, 'bg-cutscene-grassland')
      .setDisplaySize(W, H)
      .setDepth(DEPTH + 1)
      .setAlpha(0)
      // Named so the e2e can tell "the cutscene is running" from "the result
      // screen came up straight away" — the replay case has nothing else to
      // distinguish it, since a replay banks no orb.
      .setName('orb-cutscene-bg'),
  );

  // The three stargazers. Hambi Tower art, tier 1 — the plain burger, not a
  // decorated upgrade, so they read as three ordinary hambis.
  const hambis = HAMBI_XS.map((x, i) =>
    add(
      scene.add
        .image(x, GROUND_Y, 'tower-patty-1')
        .setDisplaySize(HAMBI_SIZE, HAMBI_SIZE)
        .setDepth(DEPTH + 2 + (i === 1 ? 0.1 : 0)) // centre hambi in front
        .setAlpha(0),
    ),
  );

  const orb = add(
    scene.add
      .image(ORBIT_CX, -140, 'orb-variety')
      .setDisplaySize(ORB_SIZE, ORB_SIZE)
      .setDepth(DEPTH + 3)
      .setAlpha(0),
  );

  // Drawn last so it covers everything, including the hambis and the orb.
  const flash = add(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0xffffff)
      .setDepth(DEPTH + 4)
      .setAlpha(0),
  );

  /** Tweens/timers are tracked so a skip or a teardown can stop them all. */
  const timers: Phaser.Time.TimerEvent[] = [];
  const tweens: Phaser.Tweens.Tween[] = [];
  const later = (ms: number, fn: () => void) => {
    timers.push(scene.time.delayedCall(ms, fn));
  };
  const tween = (cfg: Phaser.Types.Tweens.TweenBuilderConfig) => {
    tweens.push(scene.tweens.add(cfg));
  };

  function teardown(): void {
    for (const t of timers) t.remove(false);
    for (const t of tweens) t.stop();
    timers.length = 0;
    tweens.length = 0;
    // Kill by TARGET as well as by handle. The landing bounce and the swell are
    // started inline rather than through `tween()`, so they are not in the list
    // above — and a tween still stepping a destroyed target is how a scene's
    // tween manager quietly stops being a reliable place to schedule anything.
    for (const o of objs) scene.tweens.killTweensOf(o);
    objs.forEach((o) => o.destroy());
    objs.length = 0;
  }

  /**
   * The white cut. Everything converges here: the natural end of the orbit, a
   * click-to-skip, and nothing else. `finished` makes it idempotent, so a click
   * landing on the last frame cannot run the ending twice.
   */
  function toWhite(durationMs: number): void {
    if (finished) return;
    finished = true;
    for (const t of timers) t.remove(false);
    timers.length = 0;
    for (const t of tweens) t.stop();
    tweens.length = 0;
    scene.input.off('pointerdown', skip);

    scene.tweens.add({
      targets: flash,
      alpha: 1,
      duration: durationMs,
      ease: 'Quad.easeIn',
      onComplete: () => {
        // Peak white: the caller builds its result screen behind the flash…
        onDone();
        // …and the flash lifts off it. The flash rectangle is destroyed with
        // everything else, so the result screen is never left dimmed.
        scene.tweens.add({
          targets: flash,
          alpha: 0,
          duration: 450,
          ease: 'Quad.easeOut',
          onComplete: teardown,
        });
      },
    });
  }

  const skip = () => toWhite(260);
  scene.input.on('pointerdown', skip);

  // ── 0.0–1.5s: fade up on the meadow and its stargazers ────────────────────
  tween({ targets: bg, alpha: 1, duration: T_FADE_IN, ease: 'Quad.easeOut' });
  hambis.forEach((h, i) => {
    tween({
      targets: h,
      alpha: 1,
      duration: T_FADE_IN - 300,
      delay: 200 + i * 120,
      ease: 'Quad.easeOut',
    });
  });

  // ── 1.5–4.0s: looking around at the stars ─────────────────────────────────
  // A slow tilt with a small bob, out of phase per hambi so the three don't
  // move as one object. Yoyo+repeat runs it for the whole stargazing beat.
  later(T_FADE_IN, () => {
    hambis.forEach((h, i) => {
      tween({
        targets: h,
        angle: i === 1 ? 9 : i === 0 ? -11 : 12,
        y: GROUND_Y - 10,
        duration: 620 + i * 90,
        delay: i * 130,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    });
  });

  // ── 4.0–8.0s: the orb falls in and circles, speeding up ───────────────────
  later(T_STARGAZE_END, () => {
    orb.setAlpha(1);
    // Drop from off-screen onto the start of the orbit.
    tween({
      targets: orb,
      x: ORBIT_CX + ORBIT_RX,
      y: ORBIT_CY,
      duration: 700,
      ease: 'Quad.easeIn',
    });

    // The orbit is driven by hand rather than by a path tween: the angular
    // velocity has to RISE over the beat ("slowly speeding up"), and a tween
    // along a path can only ease its progress, not accelerate indefinitely.
    const orbitMs = T_ORBIT_END - T_STARGAZE_END - 700;
    let angle = 0;
    let elapsed = 0;
    const spin = scene.time.addEvent({
      delay: 16,
      loop: true,
      callback: () => {
        elapsed += 16;
        const p = Math.min(1, elapsed / orbitMs);
        // 0.9 → 7.5 rad/s: one lazy loop at first, a blur by the end.
        angle += (0.9 + p * p * 6.6) * 0.016;
        orb.setPosition(
          ORBIT_CX + Math.cos(angle) * ORBIT_RX,
          ORBIT_CY + Math.sin(angle) * ORBIT_RY,
        );
        // In front of the hambis on the near half of the ellipse, behind them
        // on the far half — otherwise it visibly slides over their heads.
        orb.setDepth(Math.sin(angle) > 0 ? DEPTH + 3 : DEPTH + 1.5);
        // The hambis track it, heads turning to follow the circling light.
        hambis.forEach((h) => {
          h.setAngle(Phaser.Math.Clamp((orb.x - h.x) / 22, -16, 16));
        });
      },
    });
    timers.push(spin);

    // ── 8.0–9.0s: it drops in front of them and settles ─────────────────────
    later(T_ORBIT_END - T_STARGAZE_END, () => {
      spin.remove(false);
      // Stop the stargazing loops so the settle isn't fighting them.
      for (const t of tweens) t.stop();
      tweens.length = 0;
      hambis.forEach((h) => h.setAngle(0));

      tween({
        targets: orb,
        x: ORBIT_CX,
        y: ORB_LAND_Y,
        duration: 520,
        ease: 'Quad.easeIn',
        onComplete: () => {
          // A small bounce on landing.
          scene.tweens.add({
            targets: orb,
            y: ORB_LAND_Y - 28,
            duration: 190,
            ease: 'Quad.easeOut',
            yoyo: true,
          });
          // …and it swells as the light builds.
          scene.tweens.add({
            targets: orb,
            displayWidth: ORB_SIZE * ORB_SWELL,
            displayHeight: ORB_SIZE * ORB_SWELL,
            duration: FLASH_MS,
            ease: 'Quad.easeIn',
          });
        },
      });
    });
  });

  // ── 9.0–10.0s: white light swallows the screen ────────────────────────────
  later(T_LANDED, () => toWhite(FLASH_MS));

  return () => {
    if (finished) return;
    finished = true;
    scene.input.off('pointerdown', skip);
    teardown();
  };
}
